import React, {useEffect, useMemo, useRef} from 'react'
import {observer} from 'mobx-react-lite'
import {ImageGroup} from '../../types/cms'
import {Box, Flex, Text, Button} from '../../vanilla'
import {useContentStore, useGlobalStore, usePromotionStore} from '../../store/hooks/useStore'
import {format} from 'date-fns'
import {Link} from 'react-router-dom'
import {getFullDateOutput} from '../../utils/meal-deal-utils'
import {useGTMDispatch} from '../../analytics/react-gtm-hook'
import {useIntersectionObserver} from '../../hooks/use-intersection-observer'
import {CmsImage} from '../../components/cms/cms-image'
import {Promotion} from '../../store/ProductStore'
import { PromoPills } from '../../components/promo-pill'

function creativeNameForPromotionCard(
  slotName?: unknown,
  promoName?: string,
  isMealDeal?: boolean
) {
  const promotionType = isMealDeal ? 'meal_deal_card' : 'promotion_card'

  const cleanSlotName =
    typeof slotName === 'string' ? slotName.replace(/\s+/g, '_').toLowerCase() : ''
  const cleanPromoName = promoName?.replace(/\s+/g, '_').toLowerCase() ?? ''

  return [cleanSlotName, promotionType, cleanPromoName]
    .filter((str) => Boolean(str) && typeof str === 'string')
    .join('_')
}

interface PromotionCardProps {
  /** Promotion ID */
  promoID: string
  image?: ImageGroup
  isUpcoming?: boolean
  /** Index of the promotion within overall list of on page promotions used for analytics tracking */
  slotIndex: number
  /** Index of the promotion within its slot used for analytics tracking */
  listIndex: number
  /** Name of the slot the promotion is being rendered on */
  slotName?: string
}

const PromotionCard: React.FC<PromotionCardProps> = observer(({
  promoID,
  image,
  isUpcoming,
  slotIndex,
  listIndex,
  slotName
}) => {
  const {setIsPromoModalOpen} = useGlobalStore()

  const promotionStore = usePromotionStore()

  const promo = promotionStore.promotions[promoID] ?? {}
  const ref = useRef<HTMLDivElement | null>(null)

  const cms = useContentStore()
  const mealDealBanner = promoID ? cms.contentById[promoID] : null

  const bannerImage = mealDealBanner?.image || image

  const startDate = promo?.startDate ? new Date(promo.startDate) : undefined
  const endDate = promo?.endDate ? new Date(promo?.endDate) : undefined
  const fullDateOutput = getFullDateOutput(startDate, endDate)

  const isUpcomingInner = isUpcoming || (startDate && startDate > new Date())

  const comingSoonOutput =
    startDate && isUpcomingInner ? `Coming Soon: ${format(startDate, 'dd/MM/yyyy')}` : ''
  const mealDealUrl = promo?.c_isMealDealLanding ? '/meal-deals?promo=' + promoID : ''
  const ctaText = isUpcomingInner ? 'Coming Soon' : 'View This Deal'

  // GTM Tracking
  const {isOnScreen: isTrueView} = useIntersectionObserver(ref, {
    useOnce: true,
    /* Offset for intersection track. isOnScreen will only become true as the customer scrolls and the slot comes into view.
      -70px means isOnScreen will only become true after the customer can see at least 70px of the content, preventing the
      tracking from firing before the customer has seen the content */
    rootMargin: '-70px 0px -70px 0px'
  })

  const sendDataToGTM = useGTMDispatch()

  const promotionData = useMemo(() => {
    const creativeName = creativeNameForPromotionCard(
      slotName,
      promo.name,
      promo?.c_isMealDealLanding
    )

    return [
      {
        creative_name: promo.name,
        creative_slot: creativeName,
        promotion_id: mealDealUrl || promoID,
        promotion_name: promo.name,
        slot_position: slotIndex,
        link_position: listIndex,
        campaign_ID: ''
      }
    ]
  }, [promo, slotIndex, listIndex, mealDealUrl, slotName])

  /**
   *  Track promotion view the first time card comes into the viewport.
   */
  useEffect(() => {
    if (!isTrueView || !sendDataToGTM || !promo) return

    try {
      setTimeout(() => {
        const eventData = {
          event: 'view_promotion',
          ecommerce: {
            items: promotionData
          }
        }
        /*@ts-ignore Typescript has hard time of this, GTM dispatch will be a context by this point */
        sendDataToGTM(eventData)
      })
    } catch (error) {
      console.error('Failed to send promotion view data to GTM', error.message)
    }
  }, [isTrueView, promo])

  const sendSelectEventToGTM = () => {
    try {
      const eventData = {
        event: 'select_promotion',
        ecommerce: {
          items: promotionData
        }
      }
      /*@ts-ignore GTM dispatch will be a context by this point */
      sendDataToGTM(eventData)
    } catch (error) {
      console.error('Failed to send promotion select data to GTM', error.message)
    }
  }

  const handleOnClick = (e: React.MouseEvent | React.TouchEvent) => {
    if (promo.c_isMealDealLanding) {
      return
    }

    if (!promo.id) {
      return
    }

    e.preventDefault()
    e.stopPropagation()
    sendSelectEventToGTM()
    setIsPromoModalOpen(true, {...promo, ID: promoID} as unknown as Promotion)
  }

  return (
    <Flex
      as={isUpcoming || !mealDealUrl ? 'div' : Link}
      {...(!isUpcoming && mealDealUrl && {to: mealDealUrl})}
      aria-label={promo?.name}
      bg="white"
      style={{height: '100%', borderRadius: '8px'}}
      data-test-selector="promo-list-item"
      flexWrap="wrap"
      ref={ref}
      onClick={sendSelectEventToGTM}
      data-promo-id={promoID}
    >
      {bannerImage?.images?.length && (
        <CmsImage
          images={bannerImage.images}
          useCrop="mobile"
          width="full"
          style={{objectFit: 'cover', borderTopLeftRadius: '8px', borderTopRightRadius: '8px'}}
        />
      )}
      <Box padding="16px" paddingBottom="0px">
        <Text
          variant="text6"
          textAlign={['left']}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical'
          }}
          overflow="hidden"
          color={{default: 'gray800', hover: 'gray700'}}
          marginBottom={['12px', '8px']}
          lineHeight="short"
          data-test-selector="promo-list-item-name"
        >
          {promo?.name}
        </Text>
        <PromoPills pills={promo?.c_promotionPills} />
        <Text variant="text1" color="gray1000" marginBottom={['12px', '8px']} lineHeight="shorter">
          {fullDateOutput}
        </Text>
        {comingSoonOutput && (
          <Text
            display="inline-block"
            variant="text1"
            color="gray800"
            background="infoLight"
            borderRadius="medium"
            marginBottom={['16px']}
            lineHeight="shorter"
            opacity="1"
            paddingX="8px"
            paddingY="2px"
          >
            {comingSoonOutput}
          </Text>
        )}
      </Box>
      <Flex alignSelf="flex-end" padding="16px" paddingTop="0px" width="full">
        {isUpcoming ? (
          <Button pointerEvents="none" variant="outlineDark" size="md">
            {ctaText}
          </Button>
        ) : (
          <Button
            variant="outlinePrimary"
            size="md"
            onClick={handleOnClick}
            data-test-selector="deal-view-button"
          >
            {ctaText}
          </Button>
        )}
      </Flex>
    </Flex>
  )
})

export default PromotionCard
